import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { getClubCode } from '@/lib/tagging';
import useAppointmentStore, {
  isInBranchAppointment as isInBranchAppointmentSelector,
} from '@/store/AppointmentStore';
import useContextAwarenessStore from '@/store/ContextAwarenessStore';
import useFeatureFlagsStore from '@/store/FeatureFlagsStore';
import { buildAdditionalNotes } from '../utils/buildAdditionalNotes';
import type { Confirmation } from '../utils/Confirmation';
import type { CreateAppointmentData } from '../utils/CreateAppointmentData';
import type { ProcessCustomerAccountData } from '../utils/ProcessCustomerAccountData';
import { SF_APPOINTMENT_TYPE } from '../utils/sfAppointmentTypeMap';
import { postConfirmation } from './postConfirmation';
import { postProcessCustomerAccount } from './postProcessCustomerAccount';

export const APPOINTMENT_CREATION_MUTATION_KEY = ['appointment-creation'];

export const useAppointmentCreation = () => {
  const {
    destinations,
    contactInfo,
    selectedDestination,
    searchedDestination,
    userSearch,
    selectedAgent,
    shouldUseDefaultWorktype,
    selectedDate,
    selectedStartTime,
    selectedEndTime,
    selectedAppointmentType,
    setCustomerId,
    setAccountId,
    setConfirmedAppointment,
    setAppointmentId,
    setConfirmationId,
  } = useAppointmentStore();
  const contextAwareness = useContextAwarenessStore(
    (state) => state.contextAwareness,
  );

  const clubCode = useMemo(() => getClubCode(), []);

  const useNewDestinations = useFeatureFlagsStore(
    (state) => state.featureFlags.useNewDestinations,
  );

  const inBranchApiName = useNewDestinations
    ? 'USA_Mainland'
    : 'United_States_&_Canada';

  const usaWorkTypeId =
    destinations?.find(
      (destination) => destination.destinationApiName === inBranchApiName,
    )?.workTypeId || '';

  const isInBranchAppointment = useAppointmentStore(
    isInBranchAppointmentSelector,
  );

  const workTypeId = useMemo(() => {
    if (shouldUseDefaultWorktype && isInBranchAppointment) {
      return usaWorkTypeId;
    }

    return selectedDestination.workTypeId ?? '';
  }, [
    shouldUseDefaultWorktype,
    isInBranchAppointment,
    usaWorkTypeId,
    selectedDestination,
  ]);

  const appointmentCreationMutation = useMutation({
    mutationKey: APPOINTMENT_CREATION_MUTATION_KEY,
    mutationFn: async (
      processCustomerAccountData: ProcessCustomerAccountData,
    ): Promise<Confirmation> => {
      const customerAccount = await postProcessCustomerAccount(
        processCustomerAccountData,
      );

      setCustomerId(customerAccount.customerId);
      setAccountId(customerAccount.accountId);

      const destinationNote = searchedDestination || userSearch;
      const additionalNotes = buildAdditionalNotes({
        destinationNote,
        userNotes: contactInfo.additionalInformation ?? '',
        card: contextAwareness.card,
        sourceUrl: contextAwareness.sourceUrl,
      });

      const appointmentType =
        SF_APPOINTMENT_TYPE[selectedAppointmentType ?? 'online'];
      const communicationPreference =
        contactInfo.communicationPreference ?? 'Phone';

      const createAppointmentData: CreateAppointmentData = {
        workTypeId,
        serviceTerritoryId: selectedAgent.serviceTerritoryId ?? '',
        schedStartTime: selectedStartTime ?? '',
        schedEndTime: selectedEndTime ?? '',
        appointmentType,
        communicationPreference,
        serviceResourceId: selectedAgent.resourceId ?? '',
        agentRecordId: selectedAgent.agentRecordId ?? '',
        firstName: contactInfo.firstName ?? '',
        lastName: contactInfo.lastName ?? '',
        phone: contactInfo.phoneNumber ?? '',
        email: contactInfo.email ?? '',
        zipCode: contactInfo.zipCode ?? '',
        typeOfTripDesired: contactInfo.typeOfTrip ?? [],
        whenDoYouPlanToTravel: contactInfo.expectedTravelTime ?? '',
        destinationsRequested: selectedDestination.destinationApiName ?? '',
        clubCode,
        additionalNotes,
        accountId: customerAccount.accountId ?? '',
        macsCustomerId: customerAccount.customerId ?? '',
      };

      const confirmation = await postConfirmation(createAppointmentData);

      setConfirmedAppointment({
        agentInfo: selectedAgent,
        selectedDate,
        selectedStartTime,
        selectedEndTime,
      });
      setAppointmentId(confirmation.appointmentId);
      setConfirmationId(confirmation.confirmationId);

      return confirmation;
    },
  });

  const handleStartAppointmentCreation = useCallback(() => {
    const processCustomerAccountData: ProcessCustomerAccountData = {
      firstName: contactInfo.firstName ?? '',
      lastName: contactInfo.lastName ?? '',
      phone: contactInfo.phoneNumber ?? '',
      email: contactInfo.email ?? '',
      zipCode: contactInfo.zipCode ?? '',
      clubCode,
    };

    appointmentCreationMutation.mutate(processCustomerAccountData);
  }, [
    contactInfo.firstName,
    contactInfo.lastName,
    contactInfo.phoneNumber,
    contactInfo.email,
    contactInfo.zipCode,
    clubCode,
    appointmentCreationMutation.mutate,
  ]);

  return {
    handleStartAppointmentCreation,
  };
};
